import { useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import useAnalytics from 'core/hooks/useAnalytics';

import { EVENT_NAMES } from '../../../constants/eventNames';
import feedbackService from '../../../services/feedbackService';
import type { FeedbackCategory } from '../../../services/feedbackService';

/**
 * useFeedbackModal - Owns the feedback form state (category + comment), the
 * submission against the feedback service and the success/error status dialog.
 * On success the form is reset and closing the status dialog closes the modal;
 * on error the text is kept so the user can retry.
 */

export const COMMENT_MAX_LENGTH = 1000;
const DEFAULT_CATEGORY: FeedbackCategory = 'SUGGESTION';
const FEEDBACK_CATEGORIES: readonly FeedbackCategory[] = ['SUGGESTION', 'ERROR', 'OTHER'];

export type FeedbackCategoryOption = {
  label: string;
  value: FeedbackCategory;
};

type FeedbackStatus = 'success' | 'error' | null;

type UseFeedbackModalParams = {
  isOpen: boolean;
  onClose: () => void;
};

type UseFeedbackModalReturn = {
  cancelLabel: string;
  categoryLabel: string;
  categoryOptions: FeedbackCategoryOption[];
  comment: string;
  commentLabel: string;
  commentPlaceholder: string;
  handleCategoryChange: (option: unknown) => void;
  handleClose: () => void;
  handleCommentChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  handleStatusClose: () => void;
  handleSubmit: () => void;
  isFormVisible: boolean;
  isSubmitDisabled: boolean;
  isSubmitting: boolean;
  selectedOption: FeedbackCategoryOption | null;
  status: FeedbackStatus;
  statusDescription: string;
  statusTitle: string;
  submitLabel: string;
  subtitle: string;
  title: string;
};

function isFeedbackCategory(value: unknown): value is FeedbackCategory {
  return typeof value === 'string' && (FEEDBACK_CATEGORIES as readonly string[]).includes(value);
}

function clampComment(value: string): string {
  if (value.length <= COMMENT_MAX_LENGTH) return value;

  const sliced = value.slice(0, COMMENT_MAX_LENGTH);
  const lastCode = sliced.charCodeAt(sliced.length - 1);
  const endsInHighSurrogate = lastCode >= 0xd800 && lastCode <= 0xdbff;

  return endsInHighSurrogate ? sliced.slice(0, -1) : sliced;
}

function useFeedbackModal({ isOpen, onClose }: UseFeedbackModalParams): UseFeedbackModalReturn {
  const { t } = useTranslation(['chatbot', 'actions']);
  const { trackEvent } = useAnalytics();

  const [category, setCategory] = useState<FeedbackCategory>(DEFAULT_CATEGORY);
  const [comment, setComment] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [status, setStatus] = useState<FeedbackStatus>(null);
  const submitGenerationRef = useRef<number>(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  // The modal stays mounted while hidden, so a submission settling after a
  // mid-flight cancel must not leak into the next open: every open/close
  // transition aborts the in-flight request (otherwise reopening would allow
  // a second submission while the first can still persist a duplicate) and
  // invalidates its settlement; opening resets the form.
  useEffect(() => {
    submitGenerationRef.current += 1;
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;

    if (!isOpen) return;

    setCategory(DEFAULT_CATEGORY);
    setComment('');
    setStatus(null);
    setIsSubmitting(false);
  }, [isOpen]);

  // Nothing must keep running against an unmounted modal.
  useEffect(() => () => abortControllerRef.current?.abort(), []);

  const categoryOptions = useMemo<FeedbackCategoryOption[]>(
    () => [
      { label: t('chatbot:feedback_category_suggestion'), value: 'SUGGESTION' },
      { label: t('chatbot:feedback_category_error'), value: 'ERROR' },
      { label: t('chatbot:feedback_category_other'), value: 'OTHER' },
    ],
    [t]
  );

  const selectedOption = categoryOptions.find((option) => option.value === category) ?? null;
  const isSuccess = status === 'success';

  function resetForm(): void {
    setCategory(DEFAULT_CATEGORY);
    setComment('');
  }

  function handleCategoryChange(option: unknown): void {
    const value = (option as FeedbackCategoryOption | null)?.value;
    if (!isFeedbackCategory(value)) return;

    setCategory(value);
  }

  function handleCommentChange(event: ChangeEvent<HTMLTextAreaElement>): void {
    setComment(clampComment(event.target.value));
  }

  function trackSubmission(): void {
    try {
      void Promise.resolve(trackEvent(EVENT_NAMES.USER_FEEDBACK_SUBMITTED, { category })).catch((analyticsError) => {
        console.error('Failed to track user feedback submitted event:', analyticsError);
      });
    } catch (analyticsError) {
      console.error('Failed to track user feedback submitted event:', analyticsError);
    }
  }

  async function handleSubmit(): Promise<void> {
    const trimmedComment = comment.trim();
    if (!trimmedComment || isSubmitting) return;

    const generation = submitGenerationRef.current;
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    setIsSubmitting(true);

    try {
      await feedbackService.submitFeedback({ category, comment: trimmedComment }, { signal: abortController.signal });
      if (generation !== submitGenerationRef.current) return;

      trackSubmission();
      resetForm();
      setStatus('success');
    } catch (error) {
      console.error('Error submitting feedback:', error);
      if (generation !== submitGenerationRef.current) return;

      setStatus('error');
    } finally {
      if (generation === submitGenerationRef.current) setIsSubmitting(false);
    }
  }

  function handleClose(): void {
    resetForm();
    setStatus(null);
    onClose();
  }

  function handleStatusClose(): void {
    setStatus(null);
    if (isSuccess) onClose();
  }

  return {
    cancelLabel: t('actions:Cancel'),
    categoryLabel: t('chatbot:feedback_category_label'),
    categoryOptions,
    comment,
    commentLabel: t('chatbot:feedback_comment_label'),
    commentPlaceholder: t('chatbot:feedback_comment_placeholder'),
    handleCategoryChange,
    handleClose,
    handleCommentChange,
    handleStatusClose,
    handleSubmit,
    isFormVisible: status === null,
    isSubmitDisabled: comment.trim() === '' || isSubmitting,
    isSubmitting,
    selectedOption,
    status,
    statusDescription: isSuccess ? t('chatbot:feedback_success_description') : t('chatbot:feedback_error_description'),
    statusTitle: isSuccess ? t('chatbot:feedback_success_title') : t('chatbot:feedback_error_title'),
    submitLabel: isSubmitting ? t('chatbot:Sending...') : t('actions:Send'),
    subtitle: t('chatbot:feedback_modal_subtitle'),
    title: t('chatbot:feedback_modal_title'),
  };
}

export default useFeedbackModal;
